// A3 (Фаза 8D) — «Актуальность норм»: проверка, не ссылается ли ПД на
// отменённые/заменённые нормативы. Показывается в «Целостности» (в т.ч.
// заказчику — петля приёмки до экспертизы). Паттерн = CompletenessPanel:
// фоновый прогон с полосой прогресса + таблица результата + «В замечания».
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpenCheck, RefreshCw, AlertTriangle, ExternalLink, Send, CheckCircle2, FileSearch } from 'lucide-react';
import { api } from '@/api/client';
import { DocTraceSlideOver } from '@/components/DocTracePreview';
import { PayGateBanner } from '@/components/PayGateBanner';
import { useMarkup } from '@/hooks/useMarkup';
import { MarkupToggle, MarkupFields } from '@/components/MarkupUI';
import type { Project } from '@/types/api';
import { canRunCheck, runBlockReason, tariffErrorMessage, tariffStateFromProject } from '@/lib/tariff';

interface Finding {
  id: string;
  designation: string;
  kind: string;
  status: 'active' | 'replaced' | 'cancelled' | 'not_found' | 'undated' | 'subtitle' | 'unknown';
  status_title?: string | null;
  verdict?: string | null;
  title?: string | null;
  replaced_by?: string | null;
  cancelled_at?: string | null;
  source?: string | null;
  source_url?: string | null;
  checked_at?: string | null;
  document_title?: string | null;
  document_id?: string | null; // 15.A3: якорь трассируемости на исходный документ
  quote?: string | null;
  raw_mention?: string | null;
  promoted_remark_id?: string | null;
}

interface ResultResponse {
  has_result: boolean;
  run_id?: string;
  status?: string;
  refs_count?: number;
  problems_count?: number;
  finished_at?: string | null;
  error?: string | null;
  findings: Finding[];
}

interface RunStatus {
  run_id: string;
  status: 'running' | 'done' | 'error';
  progress: { done: number; total: number; percent: number };
  refs_count: number;
  problems_count: number;
  error?: string | null;
}

const STATUS_META: Record<Finding['status'], { label: string; color: string; bg: string }> = {
  cancelled: { label: 'Отменён',       color: '#B91C1C', bg: 'rgba(239,68,68,.10)' },
  replaced:  { label: 'Заменён',       color: '#B45309', bg: 'rgba(245,158,11,.12)' },
  not_found: { label: 'Не подтверждено', color: '#6D28D9', bg: 'rgba(124,58,237,.10)' },
  undated:   { label: 'Без редакции',  color: '#0369A1', bg: 'rgba(14,165,233,.10)' },
  subtitle:  { label: 'Ист. название', color: '#4B5563', bg: 'rgba(75,85,99,.08)' },
  unknown:   { label: 'Не проверено',  color: '#6B7280', bg: 'rgba(107,114,128,.10)' },
  active:    { label: 'Действует',     color: '#047857', bg: 'rgba(16,185,129,.10)' },
};

// 8D.1: «без редакции» — информационный статус (ГОСТ Р 1.5-2012 п. 4.3.4), не проблема.
// 2026-08-12: 'not_found' тоже выведен из проблем — источник не подтвердил обозначение,
// это не факт отмены (там вперемешку опечатки в ПД и искажения распознавания).
// Показываем отдельной группой «Требуют уточнения», см. PROBLEM_STATUSES в norms_check.py.
const PROBLEM: Finding['status'][] = ['replaced', 'cancelled'];
const UNCONFIRMED: Finding['status'][] = ['not_found'];

type Filter = 'problems' | 'unconfirmed' | 'all' | 'active' | 'undated' | 'unknown';

export function NormsActualityPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [runId, setRunId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('problems');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, string>>({});
  const [tariffErr, setTariffErr] = useState<string | null>(null);
  const [traceFinding, setTraceFinding] = useState<Finding | null>(null); // 15.A3 slide-over

  // 15.E Reading B: тариф проекта — per-check гейт кнопки запуска.
  const projectQ = useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    staleTime: 1000 * 30,
  });
  const tariffState = tariffStateFromProject(projectQ.data);
  const runAllowed = canRunCheck(tariffState, 'norms');
  const runGateText = runBlockReason(tariffState, 'norms');

  const { data: result } = useQuery<ResultResponse>({
    queryKey: ['norms-actuality', projectId],
    queryFn: () => api.get<ResultResponse>(`/projects/${projectId}/norms-actuality`),
    staleTime: 30_000,
  });

  const { data: run } = useQuery<RunStatus>({
    queryKey: ['norms-actuality-run', projectId, runId],
    queryFn: () => api.get<RunStatus>(`/projects/${projectId}/norms-actuality/run/${runId}`),
    enabled: !!runId,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2000 : false),
  });

  // Прогон завершился — обновить результат и убрать полосу.
  useEffect(() => {
    if (run && run.status !== 'running') {
      qc.invalidateQueries({ queryKey: ['norms-actuality', projectId] });
      const t = setTimeout(() => setRunId(null), 1500);
      return () => clearTimeout(t);
    }
  }, [run, qc, projectId]);

  const startRun = async () => {
    try {
      setTariffErr(null);
      const resp = await api.post<{ run_id: string }>(`/projects/${projectId}/norms-actuality/run`, {});
      setRunId(resp.run_id);
    } catch (e) {
      // 15.E: тарифный гейт (402) — upsell, не сырая ошибка.
      const msg = tariffErrorMessage(e);
      setTariffErr(msg ?? (e instanceof Error ? e.message : 'Не удалось запустить проверку'));
    }
  };

  const promote = async (f: Finding) => {
    setBusy((p) => ({ ...p, [f.id]: 'promote' }));
    try {
      await api.post(`/projects/${projectId}/norms-actuality/finding/${f.id}/promote`, {});
      qc.invalidateQueries({ queryKey: ['norms-actuality', projectId] });
    } finally {
      setBusy((p) => { const n = { ...p }; delete n[f.id]; return n; });
    }
  };

  const refresh = async (f: Finding) => {
    setBusy((p) => ({ ...p, [f.id]: 'refresh' }));
    try {
      await api.post(`/projects/${projectId}/norms-actuality/finding/${f.id}/refresh`, {});
      qc.invalidateQueries({ queryKey: ['norms-actuality', projectId] });
    } finally {
      setBusy((p) => { const n = { ...p }; delete n[f.id]; return n; });
    }
  };

  const mk = useMarkup(projectId, 'norms');  // 15.G разметка заказчика
  const findings = result?.findings ?? [];
  const counts = {
    problems: findings.filter((f) => PROBLEM.includes(f.status)).length,
    unconfirmed: findings.filter((f) => UNCONFIRMED.includes(f.status)).length,
    active: findings.filter((f) => f.status === 'active').length,
    undated: findings.filter((f) => f.status === 'undated').length,
    unknown: findings.filter((f) => f.status === 'unknown').length,
  };
  const visible = findings.filter((f) => {
    if (filter === 'all') return true;
    if (filter === 'problems') return PROBLEM.includes(f.status);
    if (filter === 'unconfirmed') return UNCONFIRMED.includes(f.status);
    return f.status === filter;
  });

  const isRunning = !!runId && run?.status === 'running';

  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>
      <div className="flex items-start" style={{ gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <BookOpenCheck size={22} stroke="#0A1628" style={{ marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 260 }}>
          <h4 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>Актуальность норм</h4>
          <p className="text-muted" style={{ fontSize: 13, maxWidth: 640, lineHeight: 1.5 }}>
            Все нормативы, на которые ссылается документация, сверяются с официальными
            реестрами (Стройкомплекс.РФ, Росстандарт, pravo.gov.ru). Ссылка на отменённый
            или заменённый норматив — риск отрицательного заключения экспертизы.
          </p>
        </div>
        <button
          type="button"
          onClick={startRun}
          disabled={isRunning || !runAllowed}
          style={{
            padding: '9px 18px', borderRadius: 24, fontSize: 13, fontWeight: 600,
            background: (isRunning || !runAllowed) ? '#E5E7EB' : '#0A1628',
            color: (isRunning || !runAllowed) ? '#6B7280' : '#FFF',
            border: 'none', cursor: (isRunning || !runAllowed) ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <RefreshCw size={14} className={isRunning ? 'animate-spin' : undefined} />
          {isRunning ? 'Проверяю…' : 'Проверить нормы'}
        </button>
      </div>

      {!runAllowed && runGateText && <PayGateBanner projectId={projectId} reason={runGateText} />}
      {tariffErr && (
        <p style={{ color: '#B45309', fontSize: 13, margin: '0 0 16px', maxWidth: 720 }}>{tariffErr}</p>
      )}

      {isRunning && run && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ height: 8, borderRadius: 6, background: '#F3F4F6', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${run.progress.percent}%`,
              background: '#0A1628', transition: 'width .5s',
            }} />
          </div>
          <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
            {run.progress.done} из {run.progress.total} · первый прогон может занять несколько минут
            (живые запросы к реестрам; результаты кэшируются)
          </div>
        </div>
      )}

      {runId && run?.status === 'error' && (
        <div style={{ padding: 12, borderRadius: 8, background: 'rgba(239,68,68,.08)', color: '#B91C1C', fontSize: 13, marginBottom: 16 }}>
          Прогон завершился с ошибкой: {run.error || 'причина не указана'}
        </div>
      )}

      {!result?.has_result && !isRunning && (
        <div className="text-center text-muted" style={{ padding: '40px 20px', border: '1px dashed #E5E7EB', borderRadius: 12, fontSize: 13 }}>
          Проверка ещё не запускалась. Нажмите «Проверить нормы» — система выпишет все
          ссылки на СП, ГОСТ, СанПиН, ФЗ и постановления из документации и проверит
          статус каждой.
        </div>
      )}

      {result?.has_result && (
        <>
          <div style={{ marginBottom: 12 }}>
            <MarkupToggle on={mk.markupMode} onToggle={() => mk.setMarkupMode((v) => !v)} savedAt={mk.savedAt} />
          </div>
          {/* Сводка + фильтры */}
          <div className="flex items-center" style={{ gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {([
              ['problems', `Проблемные · ${counts.problems}`],
              ['unconfirmed', `Требуют уточнения · ${counts.unconfirmed}`],
              ['all', `Все · ${findings.length}`],
              ['active', `Действуют · ${counts.active}`],
              ['undated', `Без редакции · ${counts.undated}`],
              ['unknown', `Не проверено · ${counts.unknown}`],
            ] as [Filter, string][]).map(([id, label]) => (
              <button key={id} type="button" onClick={() => setFilter(id)}
                style={{
                  padding: '5px 12px', borderRadius: 16, fontSize: 12,
                  fontWeight: filter === id ? 600 : 400,
                  border: filter === id ? '1px solid #0A1628' : '1px solid #E5E7EB',
                  background: filter === id ? '#0A1628' : 'transparent',
                  color: filter === id ? '#FFF' : '#6B7280', cursor: 'pointer',
                }}
              >{label}</button>
            ))}
            {result.finished_at && (
              <span className="text-muted" style={{ fontSize: 11, marginLeft: 'auto' }}>
                проверено {new Date(result.finished_at).toLocaleString('ru-RU')}
              </span>
            )}
          </div>

          {counts.problems === 0 && filter === 'problems' && (
            <div className="flex items-center" style={{ gap: 10, padding: 16, borderRadius: 10, background: 'rgba(16,185,129,.06)', border: '1px solid rgba(16,185,129,.25)', fontSize: 13, color: '#047857' }}>
              <CheckCircle2 size={18} />
              Проблемных ссылок не найдено: все проверенные нормативы действуют.
            </div>
          )}

          {/* Таблица находок */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visible.map((f) => {
              const meta = STATUS_META[f.status];
              const isProblem = PROBLEM.includes(f.status);
              const isOpen = expanded === f.id;
              return (
                <div key={f.id} style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : f.id)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 14px', background: '#FFF', border: 'none',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <span className="rounded-pill" style={{
                      padding: '2px 10px', fontSize: 11, fontWeight: 700,
                      background: meta.bg, color: meta.color, whiteSpace: 'nowrap',
                    }}>{meta.label}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#0A1628', whiteSpace: 'nowrap' }}>
                      {f.designation}
                    </span>
                    <span className="text-muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {f.title || f.verdict || ''}
                    </span>
                    {f.replaced_by && (
                      <span style={{ fontSize: 11, color: '#B45309', whiteSpace: 'nowrap' }}>→ {f.replaced_by}</span>
                    )}
                    {f.promoted_remark_id && (
                      <span title="Отправлено в замечания" style={{ fontSize: 11, color: '#047857', whiteSpace: 'nowrap' }}>✓ в замечаниях</span>
                    )}
                  </button>
                  {mk.markupMode && (
                    <div style={{ padding: '0 14px 10px' }}>
                      <MarkupFields value={mk.labels[f.id]} onChange={(p) => mk.setLabel(f.id, p)} />
                    </div>
                  )}
                  {isOpen && (
                    <div style={{ padding: '12px 14px', borderTop: '1px solid #F3F4F6', background: '#FAFAFA', fontSize: 13 }}>
                      {f.verdict && (
                        <div style={{ marginBottom: 8, color: isProblem ? '#B91C1C' : '#374151', fontWeight: isProblem ? 600 : 400 }}>
                          {isProblem && <AlertTriangle size={13} style={{ display: 'inline', marginRight: 5, verticalAlign: -2 }} />}
                          {f.verdict}
                        </div>
                      )}
                      <div className="text-muted" style={{ fontSize: 12, marginBottom: 4 }}>
                        Где встречено: <b>{f.document_title || '—'}</b>
                        {f.raw_mention && <> · как написано: «{f.raw_mention}»</>}
                      </div>
                      {f.quote && (
                        <div style={{ padding: '8px 12px', borderLeft: '3px solid #E5E7EB', background: '#FFF', borderRadius: 4, fontSize: 12, color: '#374151', marginBottom: 10, lineHeight: 1.5 }}>
                          …{f.quote}…
                        </div>
                      )}
                      <div className="flex items-center text-muted" style={{ gap: 14, fontSize: 11, marginBottom: 10, flexWrap: 'wrap' }}>
                        {f.checked_at && <span>сверено: {new Date(f.checked_at).toLocaleDateString('ru-RU')}</span>}
                        {f.source && <span>источник: {f.source}</span>}
                        {f.source_url && (
                          <a href={f.source_url} target="_blank" rel="noreferrer" style={{ color: '#2563EB', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            открыть в реестре <ExternalLink size={11} />
                          </a>
                        )}
                      </div>
                      <div className="flex items-center" style={{ gap: 8 }}>
                        {(isProblem || f.status === 'undated') && !f.promoted_remark_id && (
                          <button type="button" onClick={() => promote(f)} disabled={!!busy[f.id]}
                            style={{
                              padding: '6px 14px', borderRadius: 18, fontSize: 12, fontWeight: 600,
                              background: '#0A1628', color: '#FFF', border: 'none', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 5, opacity: busy[f.id] ? 0.6 : 1,
                            }}
                          >
                            <Send size={12} /> {busy[f.id] === 'promote' ? 'Создаю…' : 'В замечания'}
                          </button>
                        )}
                        <button type="button" onClick={() => refresh(f)} disabled={!!busy[f.id]}
                          style={{
                            padding: '6px 14px', borderRadius: 18, fontSize: 12,
                            background: 'transparent', color: '#6B7280', border: '1px solid #E5E7EB',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                            opacity: busy[f.id] ? 0.6 : 1,
                          }}
                        >
                          <RefreshCw size={12} className={busy[f.id] === 'refresh' ? 'animate-spin' : undefined} />
                          {busy[f.id] === 'refresh' ? 'Проверяю…' : 'Перепроверить сейчас'}
                        </button>
                        {f.document_id && f.quote && (
                          <button type="button" onClick={() => setTraceFinding(f)}
                            title="Открыть лист документа с подсветкой ссылки на норму"
                            style={{
                              padding: '6px 14px', borderRadius: 18, fontSize: 12,
                              background: 'transparent', color: '#6B7280', border: '1px solid #E5E7EB',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                            }}
                          >
                            <FileSearch size={12} /> Показать в документе
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-muted" style={{ marginTop: 14, fontSize: 11, lineHeight: 1.5 }}>
            <b>Проблемные</b> — источник прямо подтвердил: «отменён» или «заменён». Это
            твёрдые замечания. <b>Требуют уточнения</b> («не подтверждено») — обозначение
            не подтвердилось в источниках: возможна опечатка в самой ПД или искажение при
            распознавании, а для СП — отсутствие в реестре Стройкомплекс.РФ (по нему с
            01.03.2026 работает экспертиза); проверьте написание. «Без редакции» —
            недатированная ссылка: по ГОСТ Р 1.5-2012 (п. 4.3.4) это допустимая форма,
            означающая действующую редакцию (п. 3.6.7) — информация, не замечание.
            «Не проверено» — статус не удалось установить; используйте «Перепроверить сейчас».
          </p>
        </>
      )}

      {traceFinding && (
        <DocTraceSlideOver
          title={traceFinding.designation}
          subtitle={traceFinding.document_title || null}
          badge={{ label: STATUS_META[traceFinding.status]?.label || 'Норма',
                   color: STATUS_META[traceFinding.status]?.color || '#374151',
                   bg: STATUS_META[traceFinding.status]?.bg || '#F3F4F6' }}
          bodyText={traceFinding.verdict || null}
          quote={traceFinding.quote || null}
          documentId={traceFinding.document_id}
          page={null}
          onClose={() => setTraceFinding(null)}
        />
      )}
    </div>
  );
}
